import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AuthCallback from './pages/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { Download, X } from 'lucide-react';
import './App.css';

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function InstallBanner({ deferredPrompt, onInstall, onDismiss }) {
  if (!deferredPrompt) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-stone-900 text-white rounded-xl p-4 shadow-2xl z-50 flex items-center gap-3"
      data-testid="pwa-install-banner"
      style={{ fontFamily: 'Manrope, sans-serif' }}
    >
      <div className="bg-[#E07A5F] p-2 rounded-lg flex-shrink-0">
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">Install Task Splitter</p>
        <p className="text-xs text-stone-400">Add to home screen for quick access</p>
      </div>
      <button
        onClick={onInstall}
        data-testid="pwa-install-btn"
        className="bg-[#E07A5F] hover:bg-[#D46B4E] text-white px-4 py-2 rounded-lg text-sm font-medium flex-shrink-0 transition-colors"
      >
        Install
      </button>
      <button
        onClick={onDismiss}
        data-testid="pwa-dismiss-btn"
        className="text-stone-400 hover:text-white p-1 flex-shrink-0 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Also listen for successful install
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null);
      setShowBanner(false);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" />
      </BrowserRouter>
      {showBanner && (
        <InstallBanner
          deferredPrompt={deferredPrompt}
          onInstall={handleInstall}
          onDismiss={handleDismiss}
        />
      )}
    </div>
  );
}

export default App;